import type { SchoolGoal, SchoolImplementationPlanPostBody } from '@whizz/api-model';
import type { GoalModalProps, GoalProps } from './AddEditGoalModal';
import React, { useEffect, useRef } from 'react';
import { useI18n } from '@whizz/react-i18n';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { repository } from '@whizz/api-client';
import { FieldArray, Formik, useFormikContext } from 'formik';
import debounce from 'lodash/debounce';
import { ButtonIcon, Textarea } from '../../../../components';
import { MAX_VISION_LENGTH } from '../../../../components/implementation-plan/VisionEditor';
import { QUERIES_IDS } from '../../../../util/vars';
import { useModal } from '../../../../hooks/useModal';
import useYearGroups from '../../../../hooks/useYearGroups';
import { AddEditGoalModal } from './AddEditGoalModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { AdminGoalCard } from './AdminGoalCard';

type AdminImplementationPlanValues = {
  vision: string;
  goals: SchoolGoal[];
};

// The goal modal's cohort selector expects an array of ids.
const toGoalProps = (goal: SchoolGoal): GoalProps => ({
  ...goal,
  cohort: {
    ...goal.cohort,
    id: null == goal.cohort.id ? null : [ goal.cohort.id ].flat(),
  },
});

const AutoSubmitImplementationForm = () => {
  const { values, submitForm, isSubmitting } = useFormikContext<AdminImplementationPlanValues>();
  const previousVisionRef = useRef(values.vision);
  const previousGoalsRef = useRef(JSON.stringify(values.goals));

  useEffect(() => {
    if (values.vision === previousVisionRef.current || isSubmitting) {
      return;
    }

    const autoSubmitFn = () => {
      previousVisionRef.current = values.vision;
      submitForm();
    };

    const debouncedAutoSubmit = debounce(autoSubmitFn, 600);
    debouncedAutoSubmit();

    return () => debouncedAutoSubmit.cancel();
  }, [ values.vision, submitForm, isSubmitting ]);

  useEffect(() => {
    const goalsSnapshot = JSON.stringify(values.goals);
    if (goalsSnapshot === previousGoalsRef.current || isSubmitting) {
      return;
    }

    const autoSubmitFn = () => {
      previousGoalsRef.current = goalsSnapshot;
      submitForm();
    };

    const debouncedAutoSubmit = debounce(autoSubmitFn, 600);
    debouncedAutoSubmit();

    return () => debouncedAutoSubmit.cancel();
  }, [ values.goals, submitForm, isSubmitting ]);

  return null;
};

const AdminImplementationPlan = (): JSX.Element => {
  const { translate: t } = useI18n();
  const queryClient = useQueryClient();
  const { schoolId } = useParams<{ schoolId: string }>();

  const getSchoolImplementationPlan = useQuery({
    queryKey: [ QUERIES_IDS.ADMIN.GET_SCHOOL_IMPLEMENTATION_PLAN, schoolId ],
    queryFn: () => repository.SchoolImplementationPlanRepository.get(Number(schoolId)),
  });

  const defaultGoals = useQuery({
    queryKey: [ QUERIES_IDS.ADMIN.GET_GOALS ],
    queryFn: () => repository.SchoolImplementationPlanRepository.defaultGoals(Number(schoolId)),
  });

  const [ yearGroups ] = useYearGroups(Number(schoolId));

  const classesQuery = useQuery(
    [ QUERIES_IDS.ADMIN.GET_SCHOOL_CLASSES, schoolId ],
    () => repository.AdminRepository.getSchoolClasses(Number(schoolId)),
  );

  const tagsQuery = useQuery(
    [ QUERIES_IDS.ADMIN.GET_SCHOOL_TAGS, schoolId ],
    () => repository.AdminRepository.getSchoolTags(Number(schoolId)),
  );

  const [ addEditModal, toggleAddEditModal, setAddEditModalProps ] = useModal((props: GoalModalProps) => (
    <AddEditGoalModal {...props} />
  ));

  const [ confirmModal, toggleConfirmModal, setConfirmModalProps ] = useModal((props: GoalModalProps) => (
    <DeleteConfirmModal {...props} />
  ));

  const save = useMutation(
    (values: SchoolImplementationPlanPostBody) => repository.SchoolImplementationPlanRepository.save(Number(schoolId), values),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [ QUERIES_IDS.ADMIN.GET_SCHOOLS ], exact: false });
        queryClient.invalidateQueries({ queryKey: [ QUERIES_IDS.ADMIN.GET_SCHOOL_IMPLEMENTATION_PLAN ], exact: false });
      },
    },
  );

  const handleAdd = (addGoal: (newGoal: GoalProps) => void) => () => {
    setAddEditModalProps({
      title: t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.NEW_GOAL'),
      data: null,
      defaultGoals: defaultGoals.data ?? [],
      yearGroups: yearGroups.data ?? [],
      schoolClasses: classesQuery.data?.data ?? [],
      schoolTags: tagsQuery.data?.data ?? [],
      disableMaskClick: true,
      onSuccess: addGoal,
    });

    toggleAddEditModal(true);
  };

  const handleEdit = (goal: SchoolGoal, editGoal: (goal: GoalProps) => void, removeGoal: () => void) => () => {
    const data = toGoalProps(goal);

    setAddEditModalProps({
      title: t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.EDIT_GOAL'),
      mode: 'edit',
      toggleConfirm: toggleConfirmModal,
      data,
      defaultGoals: defaultGoals.data ?? [],
      yearGroups: yearGroups.data ?? [],
      schoolClasses: classesQuery.data?.data ?? [],
      schoolTags: tagsQuery.data?.data ?? [],
      disableMaskClick: true,
      onSuccess: editGoal,
    });

    setConfirmModalProps({
      title: t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.DELETE_GOAL'),
      toggle: toggleAddEditModal,
      toggleConfirm: toggleConfirmModal,
      data,
      yearGroups: [],
      disableMaskClick: true,
      onSuccess: removeGoal,
    });

    toggleAddEditModal(true);
  };

  if (getSchoolImplementationPlan.isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {addEditModal}
      {confirmModal}
      <Formik<AdminImplementationPlanValues>
        initialValues={{
          vision: getSchoolImplementationPlan.data?.vision || '',
          goals: getSchoolImplementationPlan.data?.goals || [],
        }}
        onSubmit={(values, formikHelpers) => {
          save.mutate(values, {
            onSettled: () => {
              formikHelpers.setSubmitting(false);
            },
          });
        }}
      >
        {({ handleSubmit, setFieldValue, values }) => (
          <form onSubmit={handleSubmit} className={'space-y-4'}>
            <div className={'space-y-2'}>
              <h4 className={'text-lg font-bold'}>{t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.VISION')}</h4>
              <Textarea
                id={'vision'}
                label={''}
                value={values.vision}
                maxLength={MAX_VISION_LENGTH}
                onChange={(e) => setFieldValue('vision', e.target.value)}
              />
              <AutoSubmitImplementationForm />
            </div>
            <div className={'space-y-2'}>
              <FieldArray name={'goals'}>
                {({ push, replace, remove }) => (
                  <>
                    <div className={'flex justify-between items-center'}>
                      <h4 className={'text-lg font-bold'}>{t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.GOALS')}</h4>
                      <ButtonIcon
                        type={'button'}
                        icon={'Add'}
                        asLink
                        size={'md'}
                        color={'positive'}
                        onClick={handleAdd((newGoal: GoalProps) => push(newGoal))}
                        disabled={values.goals.length >= 3}
                      />
                    </div>
                    {values.goals.length ? (
                      values.goals.map((goal, index) => (
                        <AdminGoalCard
                          key={goal.id ?? `${goal.goal_key}-${index}`}
                          goal={goal}
                          onEdit={handleEdit(goal, (editedGoal: GoalProps) => replace(index, editedGoal), () => remove(index))}
                        />
                      ))
                    ) : (
                      <p className={'text-gray-500'}>No goals set yet.</p>
                    )}
                  </>)}
              </FieldArray>
            </div>
          </form>
        )}
      </Formik>
    </>
  );
};

export { AdminImplementationPlan };
